import { ColDef } from 'ag-grid-community';
import { CellTypes, getVehicleFilterValues } from '../../services/vehicles';
import { Vehicle } from '../../types/vehicle';

interface VehicleColDef<T> extends ColDef<T> {
  type: keyof typeof CellTypes;
  field: any;
  hide?: boolean;
  suppressFiltersToolPanel?: boolean;
  suppressColumnsToolPanel?: boolean;
}

const formatDateValue = (dateValue: string) => {
  if (dateValue && dateValue.length > 0) {
    return dateValue;
  }
  return '';
};

const formatOdometerValue = (country: string, odometer: string) => {
  if (country && country.length > 0) {
    if (country !== 'United States') {
      return `${odometer} KM`;
    }
  }
  return `${odometer} MI`;
};

const formatCPOValue = (isCertifiedPreOwned: any) => {
  if (
    (typeof isCertifiedPreOwned === 'boolean' && isCertifiedPreOwned) ||
    (typeof isCertifiedPreOwned === 'string' && isCertifiedPreOwned === 'CPO')
  ) {
    return 'CPO';
  }
  if (typeof isCertifiedPreOwned === 'boolean' && !isCertifiedPreOwned) {
    return 'Non-CPO';
  }
  return isCertifiedPreOwned;
};

const formaCpoWarrantyValue = (
  country: string,
  isCertifiedPreOwned: any,
  cpoWarrantyTermMileage: number,
  cpoWarrantyTermDuration: string
) => {
  let mileageUnit = 'MI';
  if (country && country.length > 0 && country !== 'United States') {
    mileageUnit = 'KM';
  }
  const CpoQualified =
    (typeof isCertifiedPreOwned === 'boolean' && isCertifiedPreOwned) ||
    (typeof isCertifiedPreOwned === 'string' && isCertifiedPreOwned === 'CPO');

  if (CpoQualified && cpoWarrantyTermMileage && cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermMileage} ${mileageUnit} / ${cpoWarrantyTermDuration}`;
  }
  if (CpoQualified && cpoWarrantyTermMileage && !cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermMileage} ${mileageUnit}`;
  }
  if (CpoQualified && !cpoWarrantyTermMileage && cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermDuration}`;
  }
  return '';
};

const numberRangeFilterParams = {
  filterOptions: ['inRange'],
  maxNumConditions: 1,
};

const getFilterValuesAsync = async (params: any) => {
  const col = params.colDef.field;
  const data = await getVehicleFilterValues(col);
  return params.success(data.data);
};

const dateFilterParams: any = {
  browserDatePicker: true,
  minValidDate: '2020-01-01',
  maxValidDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    const dateAsString = new Date(cellValue);

    if (dateAsString == null) {
      return 0;
    }

    // Now that both parameters are Date objects, we can compare
    if (dateAsString < new Date(filterLocalDateAtMidnight)) {
      return -1;
    }
    if (dateAsString > new Date(filterLocalDateAtMidnight)) {
      return 1;
    }
    return 0;
  },
  // filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
  filterOptions: ['inRange'],
  maxNumConditions: 1,
};

const columnDefs: VehicleColDef<Vehicle>[] = [
  // {
  //   headerName: 'ID',
  //   field: '_id',
  //   sortable: true,
  //   width: 140,
  //   filter: 'agTextColumnFilter',
  //   type: 'Plain',
  // },
  {
    headerName: 'Failed Updates',
    field: 'failedUpdates',
    hide: true,
    type: 'Plain',
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
  },
  {
    headerName: 'Batch Number',
    field: 'batchNumber',
    filter: 'agNumberColumnFilter',
    sortable: true,
    width: 140,
    type: 'Plain',
    hide: false,
    enableRowGroup: false,
  },
  {
    headerName: 'VIN',
    field: 'vin',
    sortable: true,
    width: 160,
    filter: 'agTextColumnFilter',
    type: 'Plain',
    hide: false,
    enableRowGroup: false,
  },
  {
    headerName: 'AMP1 Planned Broadcast Gate Date', // Name is intentionally changed as per business req
    field: 'actualBroadcastDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.actualBroadcastDate),
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'AMP1 Factory Gate Date ',
    field: 'actualFactoryGateDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.actualFactoryGateDate),
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'EU Port Date',
    field: 'euPortDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'AMP2 Actual Factory Gate Date',
    field: 'amp2ActualFactoryGateDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Year',
    field: 'year',
    sortable: true,
    width: 100,
    filter: 'agTextColumnFilter',
    type: 'Plain',
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Model',
    field: 'carModel',
    sortable: true,
    type: 'Plain',
    filter: 'agSetColumnFilter',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Variant',
    field: 'variant',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Exterior color',
    field: 'exteriorColor',
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Interior color',
    field: 'interiorColor',
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: getFilterValuesAsync,
      caseSensitive: true,
    },
  },
  {
    headerName: 'Appearance',
    field: 'appearance',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Roof',
    field: 'roof',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Drivetrain',
    field: 'driveTrain',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Wheels',
    field: 'wheels',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'ADAS',
    field: 'ada',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Sound System',
    field: 'soundSystem',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Front Seats',
    field: 'lucidFrontSeating',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Rear Seats',
    field: 'lucidRearSeating',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Comfort & Convenience Package',
    field: 'lucidComfortAndConvenience',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Dynamic Handling Package',
    field: 'dynamicHandlingPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Technology Package',
    field: 'technologyPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Towing Package',
    field: 'towingPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Suspension Damper',
    field: 'suspensionDamp',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Battery pack',
    field: 'batteryPack',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Price',
    field: 'totalPrice',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    sortable: true,
    type: 'CurrencyDropDown',
    hide: false,
    width: 220,
  },
  {
    headerName: 'Stage',
    field: 'vehicleStage',
    sortable: true,
    width: 140,
    type: 'Tag',
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Sub-Stage',
    field: 'vehicleSubStage',
    sortable: true,
    type: 'Plain',
    width: 160,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Category',
    field: 'category',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Sub-Category',
    field: 'subCategory',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Previous Category',
    field: 'prevAssgnChange.previousCategory',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Previous Sub-Category',
    field: 'prevAssgnChange.previousSubCategory',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Comments',
    field: 'prevAssgnChange.comments',
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'VMS Availability Status',
    field: 'vehicleSubStatus',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Location Name',
    field: 'locationName',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Transportation Confirmed',
    field: 'transportationConfirmed',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'In Transit To Location',
    field: 'inTransitToLocation',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Current Country',
    field: 'transitCountry',
    sortable: true,
    type: 'Country',
    width: 160,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Current City',
    field: 'currentCity',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Current State',
    field: 'currentState',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Factory Campaigns',
    field: 'factoryCampaignCount',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Listing Country',
    field: 'listingCountry',
    sortable: true,
    type: 'Plain',
    width: 160,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Homologation Country',
    field: 'country',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Customer Name',
    field: 'customerName',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Title Status',
    field: 'titleStatus',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Certified PreOwned',
    field: 'isCertifiedPreOwned',
    valueGetter: (params: any) => formatCPOValue(params?.data?.isCertifiedPreOwned),
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'CPO Program',
    field: 'cpoWarrantyTermMileage',
    // filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    valueGetter: (params: any) =>
      formaCpoWarrantyValue(
        params?.data?.country,
        params?.data?.isCertifiedPreOwned,
        params?.data?.cpoWarrantyTermMileage,
        params?.data?.cpoWarrantyTermDuration
      ),
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
    suppressFiltersToolPanel: true,
    filter: false,
  },
  {
    headerName: 'Sales Channel',
    field: 'salesChannel',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Odometer',
    field: 'odometer',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    valueGetter: (params: any) => formatOdometerValue(params?.data?.country, params?.data?.odometer),
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
  },
  {
    headerName: 'State of Charge',
    field: 'stateOfCharge',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
    enableRowGroup: true,
  },
  {
    headerName: 'OTA Status',
    field: 'otaStatus',
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
    enableRowGroup: true,
  },
  {
    headerName: 'OTA Version',
    field: 'otaVersion',
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
    enableRowGroup: true,
  },
  {
    headerName: 'Last Telemetry Update',
    field: 'lastTelemeteryUpdate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.lastTelemeteryUpdate),
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
    enableRowGroup: true,
  },
  {
    headerName: 'Action',
    field: 'disabled',
    sortable: true,
    type: 'Action',
    width: 100,
    enableRowGroup: false,
  },
];

export const metaData = {
  _id: '6316fd3926673f830a4d64f7',
  module: 'Vehicles',
  subModule: 'Grid',
  columnDefs,
  filterDetails: [
    'Batch·Number',
    'vin',
    'Model',
    'Variant',
    'Vehicle Stage',
    'Vehicle Substage',
    'Country',
    'Current City',
    'Current State',
    'Assigned Customer',
    'Assignment Type',
    'Seats',
    'Comfort & Convenience Package',
    'Action',
    'Transportation Confirmed',
  ],
  manageColumns: [
    'Batch Number',
    'VIN',
    'Year',
    'Model',
    'Variant',
    'Vehicle Stage',
    'Vehicle Substage',
    'Country',
    'Current City',
    'Current State',
    'Assigned Customer',
    'Assignment Type',
    'Seats',
    'Comfort & Convenience Package',
    'Transportation Confirmed',
  ],
  langCode: 'en-us',
};
